import { AddCircleOutlined, SearchOutlined } from "@mui/icons-material";
import { Box, InputAdornment, Typography } from "@mui/material";
import { type Dispatch, type FC, type SetStateAction, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemValidationTextField } from "../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { ColorDot } from "../../../../components/UIToolkit/molecules/ColorDot";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useAllJobs } from "../../../../hooks/useJobs";
import { FONT_SIZE, INK, NEUTRAL } from "../../../../theme/tokens";
import type { Job } from "../../../../utils/models";
import { useJobGroups } from "./jobGroups";
import { ManageJobsDialog } from "./ManageJobsDialog";
import { SUBGROUP_BORDERED_SX_BASE } from "./ratesConsts";
import type { GeneralFormData } from "./types";

/** Past this the list scrolls rather than pushing the integrations table's header off screen. */
const LIST_MAX_HEIGHT = 320;

/**
 * "Which jobs does this department sync?", answered where the question is asked — beside the HRIS
 * integrations that do the syncing. Read-only by design: it is the same `formData.jobIds` the Shifts
 * & Jobs tab edits, so the one edit affordance here opens the SAME `ManageJobsDialog`, writes the same
 * buffered form state, and is applied by the page's single Save. There is no second write path and no
 * endpoint of its own.
 */
export const SyncedJobsAside: FC<{
	formData: GeneralFormData;
	setFormData: Dispatch<SetStateAction<GeneralFormData>>;
	isNewDept: boolean;
}> = ({ formData, setFormData, isNewDept }) => {
	const { t } = useTranslation();
	const { chosenDepartment, refreshDepartmentData } = useHospitalData();
	const { data: allJobs = [] } = useAllJobs();
	const { groups } = useJobGroups(allJobs);
	const [filter, setFilter] = useState("");
	const [manageOpen, setManageOpen] = useState(false);

	const departmentId = chosenDepartment.department_id;
	const enabledIds = useMemo(() => new Set(formData.jobIds), [formData.jobIds]);

	/** The enabled jobs only, still in department-group order, with empty groups dropped. */
	const visibleGroups = useMemo(() => {
		const query = filter.trim().toLowerCase();
		return groups
			.map((group) => ({
				...group,
				jobs: group.jobs.filter(
					(job) =>
						enabledIds.has(job.id) &&
						// A group name matches all of its jobs, so filtering by "nursing" keeps the section whole.
						(!query || job.displayName.toLowerCase().includes(query) || group.label.toLowerCase().includes(query)),
				),
			}))
			.filter((group) => group.jobs.length > 0);
	}, [groups, enabledIds, filter]);

	const handleJobCreated = useCallback(
		(job: Job) => {
			setFormData((prev) => (prev.jobIds.includes(job.id) ? prev : { ...prev, jobIds: [...prev.jobIds, job.id] }));
			void refreshDepartmentData(departmentId, false);
		},
		[setFormData, refreshDepartmentData, departmentId],
	);

	return (
		<Box sx={{ ...SUBGROUP_BORDERED_SX_BASE, display: "flex", flexDirection: "column", overflow: "hidden" }} data-testid="synced-jobs-aside">
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, px: 1.5, py: 1.25 }}>
				<Typography sx={{ fontWeight: 600, fontSize: FONT_SIZE.EMPHASIS, color: INK.PRIMARY }}>{t("department.config.integrations.syncedJobs")}</Typography>
				<SystemButton
					buttonVariant="secondary"
					size="small"
					leadingIcon={<AddCircleOutlined fontSize="small" />}
					onClick={() => setManageOpen(true)}
					data-testid="synced-jobs-update-btn"
				>
					{t("department.config.integrations.syncedJobs.update")}
				</SystemButton>
			</Box>

			<Box
				sx={{
					display: "flex",
					alignItems: "baseline",
					justifyContent: "space-between",
					gap: 1,
					px: 1.5,
					py: 1,
					borderTop: `1px solid ${NEUTRAL[200]}`,
					borderBottom: `1px solid ${NEUTRAL[200]}`,
				}}
			>
				<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>{t("department.config.integrations.syncedJobs.includedLabel")}</Typography>
				<Typography sx={{ fontWeight: 600, fontSize: FONT_SIZE.EMPHASIS, color: INK.PRIMARY }} data-testid="synced-jobs-count">
					{t("department.config.integrations.syncedJobs.count", { count: formData.jobIds.length })}
				</Typography>
			</Box>

			<Box sx={{ px: 1.5, pt: 1.25 }}>
				<SystemValidationTextField
					value={filter}
					onChange={(event) => setFilter(event.target.value)}
					onClear={() => setFilter("")}
					placeholder={t("department.config.integrations.syncedJobs.filter")}
					reserveHeightForHelperText={false}
					inputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchOutlined fontSize="small" />
							</InputAdornment>
						),
					}}
					formControlSx={{ width: "100%" }}
					dataTestId="synced-jobs-filter"
				/>
			</Box>

			<Box sx={{ maxHeight: LIST_MAX_HEIGHT, overflowY: "auto", px: 1.5, py: 1.25, display: "flex", flexDirection: "column", gap: 1.5 }}>
				{visibleGroups.length === 0 && (
					<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }} data-testid="synced-jobs-empty">
						{t(formData.jobIds.length === 0 ? "department.config.integrations.syncedJobs.empty" : "department.config.integrations.syncedJobs.noMatches")}
					</Typography>
				)}
				{visibleGroups.map((group) => (
					<Box key={group.key} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }} data-testid={`synced-jobs-group-${group.key}`}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<ColorDot color={group.color} size="sm" dataTestId={`synced-jobs-group-dot-${group.key}`} />
							<Typography
								sx={{
									fontSize: FONT_SIZE.CAPTION,
									fontWeight: 600,
									letterSpacing: "0.06em",
									textTransform: "uppercase",
									// NEUTRAL[500], not `INK.MUTED`: the eyebrow names the bucket its rows already
									// state, so it should sit behind them. The semantic scale has no step between
									// MUTED (#525252, as dark as body text here) and DISABLED, which this is not.
									color: NEUTRAL[500],
								}}
							>
								{group.label}
							</Typography>
						</Box>
						{group.jobs.map((job) => (
							<Box key={job.id} sx={{ display: "flex", alignItems: "center", gap: 1, pl: 0.25 }} data-testid={`synced-jobs-item-${job.name}`}>
								<ColorDot color={group.color} size="xs" dataTestId={`synced-jobs-item-dot-${job.name}`} />
								<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, color: INK.PRIMARY }}>{job.displayName}</Typography>
							</Box>
						))}
					</Box>
				))}
			</Box>

			<ManageJobsDialog
				open={manageOpen}
				onClose={() => setManageOpen(false)}
				groups={groups}
				selectedJobIds={formData.jobIds}
				onConfirm={(jobIds) => setFormData((prev) => ({ ...prev, jobIds }))}
				departmentId={isNewDept ? undefined : departmentId}
				existingJobNames={allJobs.map((job) => job.name)}
				onJobCreated={handleJobCreated}
				source="integrations"
				dataTestId="synced-jobs-manage-dialog"
			/>
		</Box>
	);
};
